import React from 'react'
import PropTypes from 'prop-types'
import { Link as RouterLink } from 'react-router-dom'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import { makeStyles } from '@material-ui/core/styles'
import clsx from 'clsx'
import EmojiObjectsIcon from 'components/material-symbol/icons/EmojiObjects'
import TipWell, { TipWellActionButton } from 'components/tip-well'
import { CALLOUT_BACKGROUND } from 'lib/form-field-styles'

const CARD_BORDER = '#E0E0E0'

export const useCoachPlusPromoStyles = makeStyles(theme => ({
  wrapper: {
    marginBottom: theme.spacing(3),
  },
  wrapperSubtle: {
    marginBottom: theme.spacing(2),
  },
  sectionLayout: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
  },
  sectionPromo: {
    gap: theme.spacing(2.5),
    padding: theme.spacing(3),
    backgroundColor: '#E0EAFF',
    border: '1px solid #C5D4FF',
    borderRadius: 12,
  },
  sectionSubtle: {
    gap: theme.spacing(2),
    padding: theme.spacing(2, 2.5),
    backgroundColor: '#F5F8FF',
    border: '1px solid #E8EDF5',
    borderRadius: 10,
  },
  copy: {
    flex: '1 1 auto',
    minWidth: 0,
  },
  eyebrow: {
    fontSize: '0.6875rem',
    fontWeight: 500,
    letterSpacing: '0.08em',
    lineHeight: 1.3,
    textTransform: 'uppercase',
    color: '#6B7C87',
    marginBottom: theme.spacing(0.75),
  },
  actionButton: {
    flexShrink: 0,
    borderRadius: 20,
    textTransform: 'none',
    fontWeight: 700,
    fontSize: '0.9375rem',
    padding: theme.spacing(1, 2),
    whiteSpace: 'nowrap',
    boxShadow: 'none',
    '&:hover': {
      boxShadow: '0 4px 12px rgba(0, 83, 255, 0.28)',
    },
  },
  title: {
    fontWeight: 700,
    fontSize: '1.125rem',
    lineHeight: 1.3,
    color: '#00205B',
    marginBottom: theme.spacing(0.75),
  },
  titleSubtle: {
    fontSize: '1rem',
    marginBottom: theme.spacing(0.5),
  },
  body: {
    fontSize: '0.875rem',
    lineHeight: 1.5,
    color: '#172024',
  },
  bodySubtle: {
    fontSize: '0.8125rem',
    lineHeight: 1.45,
    color: theme.palette.text.secondary,
  },
  eyebrowSubtle: {
    marginBottom: theme.spacing(0.5),
    color: theme.palette.text.secondary,
  },
  wrapperCard: {
    marginBottom: theme.spacing(1.5),
  },
  sectionCard: {
    gap: theme.spacing(2),
    padding: theme.spacing(2, 2.5),
    backgroundColor: '#FFFFFF',
    border: `1px solid ${CARD_BORDER}`,
    borderRadius: 12,
  },
  sectionNeutral: {
    gap: theme.spacing(2),
    padding: theme.spacing(2, 2.5),
    backgroundColor: CALLOUT_BACKGROUND,
    border: '1px solid #E0E0E0',
    borderRadius: 12,
  },
  titleCard: {
    fontSize: '1rem',
    fontWeight: 700,
    color: '#172024',
    marginBottom: theme.spacing(0.5),
  },
  bodyCard: {
    fontSize: '0.8125rem',
    lineHeight: 1.4,
    color: theme.palette.text.secondary,
  },
  eyebrowCard: {
    marginBottom: theme.spacing(0.5),
    color: theme.palette.text.secondary,
  },
}))

const CoachPlusPromoBanner = ({
  eyebrow,
  title,
  body,
  actionLabel,
  actionPath,
  className,
  variant = 'default',
}) => {
  const classes = useCoachPlusPromoStyles()
  const isCoachPlus = variant === 'default' || variant === 'coachPlus'
  const isSubtle = variant === 'subtle'
  const isCard = variant === 'card'
  const isNeutral = variant === 'neutral'
  const isCompactShell = isCard || isNeutral

  return (
    <Box
      className={clsx(
        !isCompactShell && classes.wrapper,
        isSubtle && classes.wrapperSubtle,
        isCompactShell && classes.wrapperCard,
        className,
      )}
    >
      <Box
        className={clsx(
          classes.sectionLayout,
          isCard && classes.sectionCard,
          isNeutral && classes.sectionNeutral,
          isSubtle && classes.sectionSubtle,
          isCoachPlus && classes.sectionPromo,
        )}
      >
        <Box className={classes.copy}>
          {eyebrow ? (
            <Typography
              component="p"
              className={clsx(
                classes.eyebrow,
                isSubtle && classes.eyebrowSubtle,
                isCompactShell && classes.eyebrowCard,
              )}
            >
              {eyebrow}
            </Typography>
          ) : null}
          {title ? (
            <Typography
              component="h2"
              className={clsx(
                !isCompactShell && classes.title,
                isSubtle && classes.titleSubtle,
                isCompactShell && classes.titleCard,
              )}
            >
              {title}
            </Typography>
          ) : null}
          <Typography
            component="p"
            className={clsx(
              !isCompactShell && classes.body,
              isSubtle && classes.bodySubtle,
              isCompactShell && classes.bodyCard,
            )}
          >
            {body}
          </Typography>
        </Box>
        <TipWellActionButton
          component={RouterLink}
          to={actionPath}
          className={isCoachPlus ? classes.actionButton : undefined}
        >
          {actionLabel}
        </TipWellActionButton>
      </Box>
    </Box>
  )
}

CoachPlusPromoBanner.propTypes = {
  eyebrow: PropTypes.string,
  title: PropTypes.string,
  body: PropTypes.string.isRequired,
  actionLabel: PropTypes.string.isRequired,
  actionPath: PropTypes.string.isRequired,
  className: PropTypes.string,
  variant: PropTypes.oneOf([
    'default',
    'coachPlus',
    'subtle',
    'card',
    'neutral',
  ]),
}

const useBenchmarkInsightTipStyles = makeStyles(theme => ({
  wrapper: {
    '& > div': {
      alignItems: 'flex-start',
      padding: theme.spacing(2, 2.25),
      borderRadius: 10,
      gap: theme.spacing(1.75),
    },
  },
  tipCopy: {
    alignItems: 'flex-start',
  },
  tipIcon: {
    alignSelf: 'flex-start',
    lineHeight: 1,
    marginTop: 2,
  },
  tipCaption: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  tipBody: {
    fontSize: '0.875rem',
    lineHeight: 1.5,
    color: '#172024',
    fontWeight: 400,
  },
  tipHighlight: {
    paddingTop: theme.spacing(1.25),
    borderTop: '1px solid #D5DCE0',
    fontSize: '0.8125rem',
    lineHeight: 1.45,
    color: theme.palette.text.secondary,
    fontWeight: 500,
  },
}))

export const BenchmarkInsightTip = ({ body, tip }) => {
  const classes = useBenchmarkInsightTipStyles()

  return (
    <Box className={classes.wrapper}>
      <TipWell
        variant="neutral"
        icon={EmojiObjectsIcon}
        copyClassName={classes.tipCopy}
        iconClassName={classes.tipIcon}
        caption={
          <span className={classes.tipCaption}>
            <span className={classes.tipBody}>{body}</span>
            <span className={classes.tipHighlight}>{tip}</span>
          </span>
        }
      />
    </Box>
  )
}

export default CoachPlusPromoBanner
